import { useEffect, useState } from "react";
import { DataGridPro as DataGrid } from "@mui/x-data-grid-pro";
import PersonIcon from "@mui/icons-material/Person";
import NumbersIcon from "@mui/icons-material/Numbers";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import PlaceIcon from "@mui/icons-material/Place";
import DomainIcon from "@mui/icons-material/Domain";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import CoPresentIcon from "@mui/icons-material/CoPresent";
import HeadsetMicIcon from "@mui/icons-material/HeadsetMic";
import { useNavigate } from "react-router-dom";
import Translate from "../../utils/Translate";
import { dataGridLang } from "../MemToolBar";
import DotMenu from "../DotMenu";
import { CustomNoRowsOverlay } from "../../utils/StyledGridOverlay";
import { IconAndLabelColumnHeader, TableAvatar, TableAvatar2, getGridColDefForArrayValues } from "../../utils/dataGrid";
import { Tooltip } from "@mui/material";
import { useMediaQuery, useTheme } from "@mui/material";

const defaultPaginationModel = {
  pageSize: 25,
  page: 0,
};

function savePaginationModel(paginationSessionStorageKey, paginationModel) {
  if (paginationSessionStorageKey) {
    sessionStorage.setItem(paginationSessionStorageKey, JSON.stringify(paginationModel));
  }
}

function loadPaginationModel(paginationSessionStorageKey) {
  return paginationSessionStorageKey && sessionStorage[paginationSessionStorageKey]
    ? JSON.parse(sessionStorage[paginationSessionStorageKey])
    : null;
}

const getActorIcons = (type) => {
  //1 företag, 3 kontaktperson, 4 elev, 6 instruktör, 9 privatbokare
  switch (type) {
    case 3:
      return <HeadsetMicIcon />;
    case 4:
      return <PersonIcon />;
    case 6:
      return <CoPresentIcon />;
    default:
      return <PersonIcon />;
  }
};

const getRoleFilterOptions = () => [
  { value: 3, label: Translate.getActorType(3) },
  { value: 4, label: Translate.getActorType(4) },
  { value: 6, label: Translate.getActorType(6) },
];

const Comp = ({
  rows,
  smallScreen,
  getDotMenuActions,
  cardView = false,
  noRowsTransKey,
  onNavigatedToActor,
  paginationSessionStorageKey,
  restorePaginationSession,
}) => {
  const [paginationModel, setPaginationModel] = useState(
    restorePaginationSession && paginationSessionStorageKey
      ? (loadPaginationModel(paginationSessionStorageKey) ?? defaultPaginationModel)
      : defaultPaginationModel,
  );
  const localizedTextsMap = dataGridLang();
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  let columns = [];
  columns = [
    {
      field: "actorName",
      headerName: Translate.get("ActorName"),
      renderHeader: () => <IconAndLabelColumnHeader icon={PersonIcon} label={Translate.get("ActorName")} />,
      renderCell: (params) => {
        return (
          <div style={{ marginTop: "1em" }}>
            <TableAvatar2 image={params.row.profilePictureUrl} name={params.row.actorName} email={params.row.email} />
          </div>
        );
      },
      flex: 1,
      minWidth: 300,
    },

    {
      field: "externalActorNo",
      headerName: Translate.get("EmployeeNo"),
      description: Translate.get("EmployeeNo"),
      flex: 1,
      renderHeader: () => <IconAndLabelColumnHeader icon={NumbersIcon} label={Translate.get("EmployeeNo")} />,
      renderCell: (params) => {
        return <div>{params.row.externalActorNo}</div>;
      },
    },
    {
      field: "orgNo",
      headerName: Translate.get("PersonalNo"),
      flex: 1,
      renderHeader: () => <IconAndLabelColumnHeader icon={FingerprintIcon} label={Translate.get("PersonalNo")} />,
      renderCell: (params) => {
        return <div>{params.row.orgNo}</div>;
      },
    },

    {
      field: "Ort",
      headerName: Translate.get("City"),
      flex: 1,
      renderHeader: () => <IconAndLabelColumnHeader icon={PlaceIcon} label={Translate.get("City")} />,
      valueGetter: (value, row, column, apiRef) => row?.postalAddress?.actorAddressCity || "",
      sortComparator: (v1, v2) => v1.localeCompare(v2),
      display: "flex",
      renderCell: (params) => {
        return (
          <div className="stdFlexCol">
            {params.row.postalAddress?.actorAddressZipCode && <div>{params.row.postalAddress.actorAddressZipCode}</div>}
            {params.row.postalAddress?.actorAddressCity && <div>{params.row.postalAddress.actorAddressCity}</div>}
          </div>
        );
      },
    },
    {
      field: "Companies",
      headerName: Translate.get("Companies"),
      flex: 1,
      display: "flex",
      renderHeader: () => <IconAndLabelColumnHeader icon={DomainIcon} label={Translate.get("Companies")} />,
      valueGetter: (params) =>
        (params?.row?.parentActors?.length && params.row.parentActors[0]?.parentActor?.actorName) || "",
      sortComparator: (v1, v2) => v1.localeCompare(v2),
      renderCell: (params) => {
        const act1 = params.row.parentActors?.length ? params.row.parentActors[0]?.parentActor?.actorName : "";
        let act2 = "";
        if (params.row.parentActors?.length > 1 && params.row.parentActors[1]) {
          act2 = params.row.parentActors[1]?.parentActor?.actorName || "";
        }
        if (params.row.parentActors?.length > 2 && params.row.parentActors[2]) {
          act2 += "...";
        }
        return (
          <div className="stdFlexCol">
            {act1 && <div>{act1}</div>}
            {act2 && <div>{act2}</div>}
          </div>
        );
      },
    },
    {
      ...getGridColDefForArrayValues(getRoleFilterOptions()),
      field: "actorTypes",
      headerName: Translate.get("Role"),
      flex: 1,
      display: "flex",
      renderHeader: () => <IconAndLabelColumnHeader icon={AssignmentIndIcon} label={Translate.get("Role")} />,
      // Probably only needed in case someone exports the data
      valueFormatter: (value, row, column, apiRef) =>
        value && Array.isArray(value) && value.map((actorType) => Translate.getActorType(actorType)).join(", "),
      renderCell: (params) => {
        return (
          <div className="stdFlexLeft0">
            {params.row?.actorTypes?.length > 0 &&
              params.row.actorTypes.slice(0, 3).map((actorType, index) => (
                <Tooltip className="personRoles" key={index} title={Translate.getActorType(actorType)}>
                  {getActorIcons(actorType)}
                </Tooltip>
              ))}
          </div>
        );
      },
    },
    {
      field: "dotMenu",
      flex: 0.3,
      headerName: "",
      sortable: false,
      disableColumnMenu: true,
      filterable: false,
      headerClassName: "hideSeparator",
      align: "right",
      hide: !getDotMenuActions,
      hideable: false,
      renderCell: (params) =>
        getDotMenuActions && (
          <div>
            <DotMenu content={getDotMenuActions(params.row.actorId)} horizontal />
          </div>
        ),
    },
  ];

  if (cardView) {
    columns = columns.filter((column) => column.field !== "Companies" && column.field !== "actorTypes");
  }

  function handlePaginationChange(model) {
    savePaginationModel(paginationSessionStorageKey, model);
    setPaginationModel(model);
  }

  const navToUser = (id) => {
    if (onNavigatedToActor) {
      onNavigatedToActor();
    }
    navigate(`/dashboard/person/${id}`, { replace: false });
  };

  // For small screens
  const listViewColDef = {
    field: "listColumn",
    renderCell: (params) => (
      <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
        <div style={{ marginTop: "0.5em", minWidth: "300px" }}>
          <TableAvatar2
            image={params.row.profilePictureUrl}
            name={params.row.actorName}
            email={params.row.email}
            city={params.row.postalAddress.actorAddressCity}
          />
        </div>

        <div>{getDotMenuActions && <DotMenu content={getDotMenuActions(params.row.actorId)} horizontal />}</div>
      </div>
    ),
  };

  return (
    <>
      <DataGrid
        initialState={{
          sorting: {
            sortModel: [{ field: "actorName", sort: "asc" }],
          },
        }}
        rows={rows}
        columns={columns}
        disableRowSelectionOnClick
        disableColumnResize={true}
        getRowId={(row) => row.actorId}
        onRowClick={(params) => navToUser(params.id)}
        slots={{
          noRowsOverlay: CustomNoRowsOverlay(Translate.get("EmptyHereRegister")),
        }}
        slotProps={{
          toolbar: { gridId: "Users" },
          columnsPanel: { className: "customColumnPanel" },
          filterPanel: { className: "customfilterPanel" },
          panel: { className: "customPanel" },
        }}
        rowHeight={isSmallScreen ? 70 : 65}
        localeText={localizedTextsMap}
        pagination
        pageSizeOptions={[25, 100, 250]}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationChange}
        disableDensitySelector={true}
        disableColumnMenu
        listView={isSmallScreen}
        listViewColumn={isSmallScreen ? listViewColDef : undefined}
      />
    </>
  );
};

export default Comp;
